import {
  ComponentPropsWithoutRef,
  ReactNode,
  useEffect,
  useState,
} from "react";
import { CoursesExtras, useCoursesExtras } from "../../homeContext.ts";
import BaseLinkOrButton from "../../../../common/core-ui/BaseLinkOrButton.tsx";
import MiscellaneousMail from "../../../../common/icons/MiscellaneousMail.tsx";
import { useIntl } from "react-intl";
import MiscellaneousExternalWindow from "../../../../common/icons/MiscellaneousExternalWindow.tsx";
import styles from "./GrayButtonList.module.scss";
import { clsx } from "clsx";
import { useBrandLoaderData } from "../../../root/rootLoader.ts";
import useStoreLinks from "../../../../common/features/store/useStoreLinks.ts";
import MiscellaneousTag from "../../../../common/icons/MiscellaneousTag.tsx";
import { getAuthenticatedRedirectLink } from "../../../user/redirect/redirectHelpers.ts";
import { DeweySchema } from "../../../root/helpers/brandSchema.ts";

type BaseLinkOrButtonProps = ComponentPropsWithoutRef<typeof BaseLinkOrButton>;

const customButtonIconMap: Record<
  DeweySchema["CustomButton"]["icon"],
  ReactNode
> = {
  Tag: <MiscellaneousTag />,
  "": null,
};

interface GrayButton {
  icon: ReactNode | undefined;
  label: string;
  href: string;
  enabled: boolean;
}

type InstructorLinksProps = Pick<
  CoursesExtras,
  "isInstructor" | "analyticsUrl"
>;

export default function GrayButtonList({
  className,
  linkClassName,
  showIcons = true,
}: {
  className?: string;
  linkClassName?: string;
  showIcons?: boolean;
}) {
  const data = useCoursesExtras();
  const brand = useBrandLoaderData();
  const { isStoreEnabled, makeBasicStoreURL } = useStoreLinks();
  const intl = useIntl();
  const [links, setLinks] = useState<GrayButton[]>([]);

  function renderListItem({
    label,
    icon,
    href,
  }: {
    label: string;
    icon?: ReactNode;
    href: string;
  } & BaseLinkOrButtonProps) {
    return (
      <li key={label}>
        <BaseLinkOrButton href={href} className={linkClassName}>
          {showIcons && icon}
          {label}
        </BaseLinkOrButton>
      </li>
    );
  }

  useEffect(() => {
    const defaultLinks = (): Promise<GrayButton[]> => {
      return Promise.resolve([
        {
          icon: customButtonIconMap[brand.CustomButton.icon],
          label: brand.CustomButton.label,
          href: brand.CustomButton.sso
            ? getAuthenticatedRedirectLink(brand.CustomButton.url)
            : brand.CustomButton.url,
          enabled: brand.CustomButton.enabled,
        },
        /**
         * @see common/header/StoreButton
         */
        {
          icon: <MiscellaneousTag />,
          label: intl.formatMessage({
            defaultMessage: "Store",
          }),
          href: makeBasicStoreURL({ utm_medium: "bookshelf-home-left-nav" })!,
          enabled: isStoreEnabled,
        },
      ]);
    };

    const instructorLinks = ({
      isInstructor,
      analyticsUrl,
    }: InstructorLinksProps): GrayButton[] => {
      if (isInstructor && analyticsUrl) {
        return [
          {
            label: intl.formatMessage({
              defaultMessage: "Instructor Analytics",
            }),
            href: analyticsUrl,
            icon: null,
            enabled: true,
          },
        ];
      } else {
        return [];
      }
    };

    const myCoursesLinks = (): Promise<GrayButton[]> => {
      return data.then(({ links, isInstructor, analyticsUrl }) => {
        return links
          .filter(({ href, label }) => href && label)
          .map(({ href, label }): GrayButton => {
            const icon = href.startsWith("mailto:") ? (
              <MiscellaneousMail />
            ) : (
              <MiscellaneousExternalWindow />
            );
            return {
              icon,
              label,
              href,
              enabled: true,
            };
          })
          .concat(instructorLinks({ isInstructor, analyticsUrl }));
      });
    };

    const virtualStoreLinks = (): Promise<GrayButton[]> => {
      const vsLinks: GrayButton[] = [
        {
          icon: <MiscellaneousExternalWindow />,
          label:
            brand.VirtualStore.Merch.text ||
            intl.formatMessage({ defaultMessage: "Apparel & Gifts" }),
          // URL should always be configured if enabled, fallback for typing
          href: brand.VirtualStore.Merch.url ?? "#",
          enabled: brand.VirtualStore.Merch.enabled,
        },
        {
          icon: <MiscellaneousExternalWindow />,
          label: intl.formatMessage({ defaultMessage: "Sell Books" }),
          href: brand.VirtualStore.SellBooks.url,
          enabled: brand.VirtualStore.SellBooks.enabled,
        },
      ];
      return data.then(({ isInstructor, analyticsUrl }) => {
        return vsLinks.concat(instructorLinks({ isInstructor, analyticsUrl }));
      });
    };

    const linksPromise = brand.VirtualStore.companyId
      ? virtualStoreLinks()
      : brand.MyCourses.enabled
      ? myCoursesLinks()
      : defaultLinks();

    linksPromise.then((l) => setLinks(l)).catch(() => setLinks([]));
  }, [brand, data, intl, isStoreEnabled, makeBasicStoreURL]);

  if (!links.length) {
    return null;
  }

  return (
    <ul className={clsx(className, styles.list)}>
      {links.filter((el) => el.enabled).map((link) => renderListItem(link))}
    </ul>
  );
}
